// libraries
import { nanoid } from 'nanoid';
import { RSAA } from 'redux-api-middleware';
import { paymentPayloadUtils } from '@makemydeal/dr-offer-redux';
import { ApplicationCodes, LEASE, PaymentScheduleTypes } from '@makemydeal/dr-platform-types';
import { TRACE_ID_HEADER } from '@makemydeal/dr-shared-bff-types';

// types
import type { DashStoreThunk } from '../types/appState';
import type { FirstPencilProgramQuoteRequest, FirstPencilProgramsOfferType } from '../types/firstPencilTypes';

// consts/enums
import {
    FIRST_PENCIL_PROGRAMS_FAILURE,
    FIRST_PENCIL_PROGRAMS_LOAD_STARTED,
    FIRST_PENCIL_PROGRAMS_REQUEST,
    FIRST_PENCIL_PROGRAMS_SUCCESS
} from '../actionTypes/firstPencilActionTypes';

// selectors
import * as firstPencilSelectors from '../selectors/firstPencilSelectors';

// utils
import { buildPaymentPayloadHostSelectors } from '../utils/offerPayloadUtil';
import { getProgramOrchestratorUrl } from '../utils/paymentServiceUtils';

export const programsLoadStarted = (offerType: FirstPencilProgramsOfferType, loadId: string, requestCount: number) => ({
    type: FIRST_PENCIL_PROGRAMS_LOAD_STARTED,
    payload: { offerType, loadId, requestCount }
});

type FirstPencilOfferNode = { offerPrice?: number; paymentFrequency?: string; daysToFirstPayment?: number };

type FirstPencilPayload = {
    vehicle?: { listedPrice?: number; retailPrice?: number };
    payment?: Partial<Record<FirstPencilProgramsOfferType, FirstPencilOfferNode>>;
};

const DEFAULT_DAYS_TO_FIRST_PAYMENT = 30;

const withFirstPencilDefaults = <T extends FirstPencilPayload>(payload: T, offerType: FirstPencilProgramsOfferType): T => {
    const offerNode = payload.payment?.[offerType];
    if (!offerNode) {
        return payload;
    }
    const vehiclePrice = payload.vehicle?.listedPrice || payload.vehicle?.retailPrice;
    if (vehiclePrice) {
        offerNode.offerPrice = vehiclePrice;
    }
    if (offerType === LEASE) {
        offerNode.paymentFrequency ||= PaymentScheduleTypes.Monthly;
        offerNode.daysToFirstPayment ??= DEFAULT_DAYS_TO_FIRST_PAYMENT;
    }
    return payload;
};

export const loadFirstPencilProgramQuotes =
    (offerType: FirstPencilProgramsOfferType, requests: FirstPencilProgramQuoteRequest[] = [{}]): DashStoreThunk =>
    (dispatch, getState) => {
        const state = getState();
        if (!firstPencilSelectors.isFirstPencilFlowActive(state)) {
            return;
        }

        const loadId = nanoid();
        const vehicleOverride = firstPencilSelectors.getSelectedVehicle(state);
        const hostSelectors = buildPaymentPayloadHostSelectors();
        const endpoint = getProgramOrchestratorUrl(state);

        dispatch(programsLoadStarted(offerType, loadId, requests.length));

        requests.forEach(({ creditScoreOverride, requestedTier, primary }) => {
            const isNonPrimaryTierCall = requestedTier != null && primary !== true;

            const payload = withFirstPencilDefaults(
                paymentPayloadUtils.createPaymentPayload(
                    state,
                    hostSelectors,
                    {
                        type: FIRST_PENCIL_PROGRAMS_REQUEST,
                        meta: {
                            isProgramCall: true,
                            sourceApplication: ApplicationCodes.MV,
                            ...(creditScoreOverride != null && { creditScoreOverride }),
                            ...(requestedTier != null && { requestedTier, primary }),
                            includeIncentives: !isNonPrimaryTierCall,
                            vehicleOverride
                        }
                    },
                    offerType
                ),
                offerType
            );

            const meta = { offerType, loadId, requestedTier, primary, creditScoreOverride };

            dispatch({
                [RSAA]: {
                    endpoint,
                    method: 'POST',
                    body: JSON.stringify(payload),
                    headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: payload.traceId ?? '' },
                    types: [
                        { type: FIRST_PENCIL_PROGRAMS_REQUEST, meta },
                        { type: FIRST_PENCIL_PROGRAMS_SUCCESS, meta },
                        { type: FIRST_PENCIL_PROGRAMS_FAILURE, meta }
                    ]
                }
            });
        });
    };
